// src/pages/ManutenzioniAssegnate.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { generateManutenzionePdfBlob } from '../pdf/clientPdf';
import { salvaOffline, senzaRete, TIPI } from '../offline/salvaOffline';
import { Card, Checkbox } from '../components/ui';
import DatiTrasferta, { CAMPI_TRASFERTA } from '../components/DatiTrasferta';

const GAS_API_BASE = '/api';
const API_BASE = '/api';

const templates = [
  {
    id: 'CIESSE_IMPIANTI',
    label: 'Ciesse Impianti',
    file: '/Manutenzione/CiesseImpianti.pdf',
  },
  {
    id: 'CIESSE_SERVICE',
    label: 'Ciesse Service',
    file: '/Manutenzione/CiesseService.pdf',
  },
];

// Testi personalizzabili per le verifiche 1–8
const CHECK_LABELS = {
  1: 'Verifica 1: Struttura esterna: verifica visiva di eventuali danneggiamenti o rotture',
  2: 'Verifica 2: Guarnizione porta: verifica visiva del corretto montaggio e presenza di crepe o fessure',
  3: 'Verifica 3: Chiusura porta e interruttore: controllo efficienza di tenuta della guarnizione porta-verifica della chiusura e dell’allineamento',
  4: 'Verifica 4: Struttura interna e sistema di ventilazione: controllo visivo delle condizioni',
  5: 'Verifica 5: Cablaggi, connessioni elettriche e dispositivi interni: controllo visivo e manuale',
  6: 'Verifica 6: Teleruttori, Relè e Relè a stato solido: verifica visiva e stato dei contatti',
  7: 'Verifica 7: Controllo e pulizia condensatore: con aspiratore',
  8: 'Verifica 8: Verifica assorbimento elettrico',
};


// Nome visuale dell'utente per le notifiche (senza email)
function getCleanUserName(user) {
  if (!user) return '';

  const candidates = [];

  if (user.username) candidates.push(user.username);
  if (user.nome || user.cognome) {
    candidates.push(`${user.nome || ''} ${user.cognome || ''}`.trim());
  }
  if (user.fullName) candidates.push(user.fullName);

  let name = candidates.find(Boolean) || '';

  name = name
    .split(/\s+/)
    .filter((part) => !part.includes('@'))
    .join(' ')
    .trim();

  if (name) return name;

  if (user.email && user.email.includes('@')) {
    const local = user.email.split('@')[0];
    return local.replace('.', ' ').trim();
  }

  return '';
}

function formatNow() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} alle ore ${hh}:${min}`;
}

const ManutenzioniAssegnate = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const fromWorkOrder = location.state?.fromWorkOrder || {};
  const savedFormData = fromWorkOrder.formData || {};
  const workOrderId = location.state?.workOrderId || null;

  const [selectedTemplate, setSelectedTemplate] = useState('CIESSE_IMPIANTI');

  // ======================
  // FORM DATA PRECOMPILATO (stesse chiavi di VitAssegnati.jsx)
  // ======================
  const [formData, setFormData] = useState(() => {
    // Data intervento
    let data = '';
    if (fromWorkOrder.dataIntervento) {
      const d = new Date(fromWorkOrder.dataIntervento);
      if (!Number.isNaN(d.getTime())) {
        data = d.toISOString().slice(0, 10);
      }
    } else if (fromWorkOrder.data) {
      data = fromWorkOrder.data;
    } else if (fromWorkOrder.dateIntervento) {
      const d = new Date(fromWorkOrder.dateIntervento);
      if (!Number.isNaN(d.getTime())) {
        data = d.toISOString().slice(0, 10);
      }
    }

    return {
      // `data` non si mette qui: verrebbe sovrascritta da ...savedFormData e
      // dalla riga in fondo, che e' quella con l'intento giusto.

      // stesso campo che usa VitAssegnati per cliente
      cliente: fromWorkOrder.cliente || fromWorkOrder.companyName || '',
      // stesso concetto di "impianto / presidio / reparto"
      impianto:
        fromWorkOrder.impianto ||
        fromWorkOrder.presidio ||
        fromWorkOrder.reparto ||
        fromWorkOrder.equipmentPresidio ||
        '',
      // stesso concetto di "luogo intervento"
      luogo:
        fromWorkOrder.luogo ||
        fromWorkOrder.luogoIntervento ||
        fromWorkOrder.indirizzo ||
        fromWorkOrder.place ||
        '',
      // note riempite con la descrizione della lavorazione
      note:
        fromWorkOrder.descrizione ||
        fromWorkOrder.noteGenerali ||
        fromWorkOrder.description ||
        '',

      modello: fromWorkOrder.modello || fromWorkOrder.equipmentModel || '',
      matricola: fromWorkOrder.seriale || fromWorkOrder.equipmentSerial || '',
      inventario: fromWorkOrder.inventario || fromWorkOrder.equipmentInventory || '',
      codiceInterno: fromWorkOrder.codiceInterno || fromWorkOrder.internalCode || '',

      ...CAMPI_TRASFERTA,

      // 🔥 DATI GIÀ COMPILATI VINCONO
      ...savedFormData,

      // Deve stare DOPO lo spread: se il rapportino salvato ha la data vuota
      // si torna a quella calcolata dalla lavorazione, invece di restare senza.
      data: savedFormData.data || data,
    };
  });

  const savedChecks = fromWorkOrder.checks || {};

  // 15 check; 9,10,12,13,14 hanno anche NA; 9,12,13 hanno valori
  const [checks, setChecks] = useState({
    1: { ok: false },
    2: { ok: false },
    3: { ok: false },
    4: { ok: false },
    5: { ok: false },
    6: { ok: false },
    7: { ok: false },
    8: { ok: false, note: '' },
    9: { ok: false, na: false, value: '' },
    10: { ok: false, na: false },
    11: { ok: false },
    12: { ok: false, na: false, tSet: '', tMis: '' },
    13: { ok: false, na: false, urSet: '', urMis: '' },
    14: { ok: false, na: false },
    15: { ok: false, note: '' },

    // 🔥 merge dei check salvati
    ...savedChecks,
  });

  const [tecnicoSignature, setTecnicoSignature] = useState(
    fromWorkOrder.tecnicoSignature || null
  );
  const [clienteSignature, setClienteSignature] = useState(
    fromWorkOrder.clienteSignature || null
  );
  
  const [signModal, setSignModal] = useState({ open: false, type: null });
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pdfUrl, setPdfUrl] = useState(
    templates.find((t) => t.id === selectedTemplate)?.file || null
  );

  // ======================
  // GAS UTILIZZATO / RECUPERATO
  // ======================
  const [gasUsedName, setGasUsedName] = useState('');
  const [gasUsedAmount, setGasUsedAmount] = useState('');
  const [gasRecoveredName, setGasRecoveredName] = useState('');
  const [gasRecoveredAmount, setGasRecoveredAmount] = useState('');
  const [availableGases, setAvailableGases] = useState([]);

  // Carica i gas disponibili sul furgone dell'utente
  useEffect(() => {
    const loadMyGases = async () => {
      try {
        const meRes = await authFetch(`${GAS_API_BASE}/user`);
        if (!meRes.ok) return;
        const me = await meRes.json();
        const currentUserId = me.id || me._id;

        const gasRes = await authFetch(`${GAS_API_BASE}/gas`);
        if (!gasRes.ok) return;
        const all = await gasRes.json();

        const myNames = Array.from(
          new Set(
            all
              .filter((g) => {
                const uid = g.user?._id || g.user?.id;
                return uid === currentUserId;
              })
              .map((g) => g.gasName)
          )
        ).sort();
        setAvailableGases(myNames);
      } catch (err) {
        console.error(
          'Errore caricamento gas disponibili (MANUTENZIONI ASSEGNATE):',
          err
        );
      }
    };

    loadMyGases();
  }, []);

  // aggiorna anteprima PDF
  useEffect(() => {
    const t = templates.find((tmp) => tmp.id === selectedTemplate);
    setPdfUrl(t ? t.file : null);
  }, [selectedTemplate]);

  // blocca scroll quando la modale firme è aperta
  useEffect(() => {
    if (signModal.open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
    return undefined;
  }, [signModal.open]);

  useEffect(() => {
    const loadWip = async () => {
      try {
        if (!workOrderId) return;
      
        const res = await authFetch(
          `${API_BASE}/work/in-progress/by-workorder/${workOrderId}`
        );
      
        // Se non esiste WIP, pace (rimani coi dati base)
        if (!res.ok) {
          console.warn('WIP non trovato o errore:', res.status);
          return;
        }
      
        const wip = await res.json();
        console.log('WIP caricato:', wip);
      
        // ⚠️ Se il backend wrappa la risposta, gestisci entrambi
        const data = wip?.workInProgress || wip;
      
        if (data?.formData) {
          setFormData((prev) => ({
            ...prev,
            ...data.formData,
            inventario:
              data.formData?.inventario ||
              prev.inventario ||
              fromWorkOrder.inventario ||
              fromWorkOrder.equipmentInventory ||
              '',
            data: data.formData.data || prev.data,
          }));
        }
      
        if (data?.checks) {
          setChecks((prev) => ({
            ...prev,
            ...data.checks,
          }));
        }
      
        if (data?.tecnicoSignature) setTecnicoSignature(data.tecnicoSignature);
        if (data?.clienteSignature) setClienteSignature(data.clienteSignature);
      } catch (err) {
        console.error('Errore recupero WorkInProgress (ManutenzioniAssegnate):', err);
      }
    };
  
    loadWip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);
  


  // ======================
  // HANDLER FORM / CHECK / FIRMA
  // ======================

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleCheck = (num, field) => {
  setChecks((prev) => {
    const current = prev[num] || {};
    const nextValue = !current[field];

    const next = {
      ...prev,
      [num]: {
        ...current,
        [field]: nextValue,
      },
    };

    // ✅ Mutua esclusione OK ↔ NA
    if (field === 'ok' && nextValue === true && 'na' in current) {
      next[num].na = false;
    }

    if (field === 'na' && nextValue === true) {
      next[num].ok = false;

      // ✅ se metti NA, pulisco eventuali valori
      if ('value' in current) next[num].value = '';
      if ('tSet' in current) next[num].tSet = '';
      if ('tMis' in current) next[num].tMis = '';
      if ('urSet' in current) next[num].urSet = '';
      if ('urMis' in current) next[num].urMis = '';
      if ('note' in current) next[num].note = '';
    }

    // ✅ se togli OK alla 8, pulisco la nota
    if (num === 8 && field === 'ok' && nextValue === false) {
      next[num].note = '';
    }

    return next;
  });
};

  

  const handleCheckValueChange = (num, field, value) => {
    setChecks((prev) => ({
      ...prev,
      [num]: {
        ...prev[num],
        [field]: value,
      },
    }));
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveDraw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    ctx.lineTo(x, y);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const endDraw = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const openSignatureModal = (type) => {
    setSignModal({ open: true, type });
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      //ctx.fillStyle = '#ffffff';
      //ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, 50);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL('image/png');

    if (signModal.type === 'tecnico') {
      setTecnicoSignature(dataUrl);
    } else if (signModal.type === 'cliente') {
      setClienteSignature(dataUrl);
    }

    setSignModal({ open: false, type: null });
  };

  const clearSignaturePreview = (type) => {
    if (type === 'tecnico') {
      setTecnicoSignature(null);
    } else if (type === 'cliente') {
      setClienteSignature(null);
    }
  };

  // ======================
  // GAS: QU / QR / QF (stessa logica di VitAssegnati.jsx)
  // ======================
  const consumeGasAndNotify = async () => {
    const usedName = gasUsedName.trim();
    const recoveredName = gasRecoveredName.trim();

    const usedAmountRaw = String(gasUsedAmount || '').replace(',', '.');
    const recoveredAmountRaw = String(gasRecoveredAmount || '').replace(',', '.');

    const usedAmount = Math.round(parseFloat(usedAmountRaw));
    const recoveredAmount = Math.round(parseFloat(recoveredAmountRaw));

    const hasUsed = usedName && !Number.isNaN(usedAmount) && usedAmount > 0;
    const hasRecovered =
      recoveredName && !Number.isNaN(recoveredAmount) && recoveredAmount > 0;

    if (!hasUsed && !hasRecovered) return;

    try {
      const meRes = await authFetch(`${GAS_API_BASE}/user`);
      if (!meRes.ok) {
        console.error(
          'Impossibile recuperare utente per aggiornare i gas (MANUTENZIONI ASSEGNATE)'
        );
        return;
      }
      const me = await meRes.json();
      const currentUserId = me.id || me._id;

      const gasRes = await authFetch(`${GAS_API_BASE}/gas`);
      if (!gasRes.ok) {
        console.error(
          'Impossibile recuperare livelli gas (MANUTENZIONI ASSEGNATE)'
        );
        return;
      }
      const gasData = await gasRes.json();

      const messageParts = [];

      // CASO 1: stesso gas o solo usato / solo recuperato → formula QU / QR / QF
      const sameGas =
        hasUsed &&
        hasRecovered &&
        usedName &&
        recoveredName &&
        usedName === recoveredName;

      if (sameGas || (hasUsed && !hasRecovered) || (!hasUsed && hasRecovered)) {
        const singleName =
          (sameGas && usedName) ||
          (hasUsed && usedName) ||
          (hasRecovered && recoveredName);

        const QU = hasUsed ? usedAmount : 0;
        const QR = hasRecovered ? recoveredAmount : 0;

        let target = gasData.find((g) => {
          const uid = g.user?._id || g.user?.id;
          return uid === currentUserId && g.gasName === singleName;
        });

        let QF = target ? Number(target.quantityGrams || 0) : 0;

        if (!target && QU > QR) {
          console.warn(
            'Tentativo di usare più gas di quello presente (nessun record) per',
            singleName
          );
        } else {
          const QF_new = QF + QR - QU;

          if (QU === QR && QU > 0) {
            console.log(`QU == QR per ${singleName}, QF resta ${QF} g.`);
          } else {
            if (!target) {
              const createRes = await authFetch(`${GAS_API_BASE}/gas`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  gasName: singleName,
                  quantityGrams: Math.max(0, QF_new),
                }),
              });

              if (!createRes.ok) {
                const data = await createRes.json().catch(() => null);
                console.error(
                  'Errore creazione gas (MANUTENZIONI ASSEGNATE, QU/QR/QF):',
                  data || createRes.status
                );
              } else {
                console.log(
                  `Gas "${singleName}" creato con ${Math.max(
                    0,
                    QF_new
                  )} g (MANUTENZIONI ASSEGNATE, QU/QR/QF) per utente`,
                  currentUserId
                );
              }
            } else {
              const updateRes = await authFetch(
                `${GAS_API_BASE}/gas/${target._id}`,
                {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ quantityGrams: Math.max(0, QF_new) }),
                }
              );

              if (!updateRes.ok) {
                const data = await updateRes.json().catch(() => null);
                console.error(
                  'Errore aggiornamento livello gas (MANUTENZIONI ASSEGNATE, QU/QR/QF):',
                  data || updateRes.status
                );
              } else {
                console.log(
                  `Gas "${singleName}" aggiornato da ${QF} g a ${Math.max(
                    0,
                    QF_new
                  )} g (MANUTENZIONI ASSEGNATE, QU/QR/QF) per utente`,
                  currentUserId
                );
              }
            }
          }

          if (hasUsed) messageParts.push(`utilizzati ${QU} g di ${singleName}`);
          if (hasRecovered)
            messageParts.push(`recuperati ${QR} g di ${singleName}`);
        }
      }

      // CASO 2: gas diversi → fallback logica separata
      if (
        hasUsed &&
        hasRecovered &&
        usedName &&
        recoveredName &&
        usedName !== recoveredName
      ) {
        // consumo
        const targetUsed = gasData.find((g) => {
          const uid = g.user?._id || g.user?.id;
          return uid === currentUserId && g.gasName === usedName;
        });

        if (!targetUsed) {
          console.warn(
            'Nessun gas con questo nome per il tuo furgone (uso MANUTENZIONI ASSEGNATE, gas diverso):',
            usedName
          );
        } else {
          const currentQty = Number(targetUsed.quantityGrams || 0);
          const newQty = Math.max(0, currentQty - usedAmount);

          const updateRes = await authFetch(
            `${GAS_API_BASE}/gas/${targetUsed._id}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quantityGrams: newQty }),
            }
          );

          if (!updateRes.ok) {
            const data = await updateRes.json().catch(() => null);
            console.error(
              'Errore aggiornamento livello gas (uso MANUTENZIONI ASSEGNATE, gas diverso):',
              data || updateRes.status
            );
          } else {
            console.log(
              `Gas "${usedName}" aggiornato da ${currentQty} g a ${newQty} g (uso MANUTENZIONI ASSEGNATE, gas diverso) per utente`,
              currentUserId
            );
            messageParts.push(`utilizzati ${usedAmount} g di ${usedName}`);
          }
        }

        // recupero
        let targetRec = gasData.find((g) => {
          const uid = g.user?._id || g.user?.id;
          return uid === currentUserId && g.gasName === recoveredName;
        });

        if (!targetRec) {
          const createRes = await authFetch(`${GAS_API_BASE}/gas`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              gasName: recoveredName,
              quantityGrams: recoveredAmount,
            }),
          });

          if (!createRes.ok) {
            const data = await createRes.json().catch(() => null);
            console.error(
              'Errore creazione gas per recupero (MANUTENZIONI ASSEGNATE, gas diverso):',
              data || createRes.status
            );
          } else {
            targetRec = await createRes.json();
            console.log(
              `Gas "${recoveredName}" creato con ${recoveredAmount} g (recupero MANUTENZIONI ASSEGNATE, gas diverso) per utente`,
              currentUserId
            );
            messageParts.push(
              `recuperati ${recoveredAmount} g di ${recoveredName}`
            );
          }
        } else {
          const currentQtyRec = Number(targetRec.quantityGrams || 0);
          const newQtyRec = currentQtyRec + recoveredAmount;

          const updateRes = await authFetch(
            `${GAS_API_BASE}/gas/${targetRec._id}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quantityGrams: newQtyRec }),
            }
          );

          if (!updateRes.ok) {
            const data = await updateRes.json().catch(() => null);
            console.error(
              'Errore aggiornamento livello gas (recupero MANUTENZIONI ASSEGNATE, gas diverso):',
              data || updateRes.status
            );
          } else {
            console.log(
              `Gas "${recoveredName}" aggiornato da ${currentQtyRec} g a ${newQtyRec} g (recupero MANUTENZIONI ASSEGNATE, gas diverso) per utente`,
              currentUserId
            );
            messageParts.push(
              `recuperati ${recoveredAmount} g di ${recoveredName}`
            );
          }
        }
      }

      if (messageParts.length > 0) {
        const displayName = getCleanUserName(me);
        const when = formatNow();

        const message = displayName
          ? `${displayName} ${messageParts.join(
              ' e '
            )} durante una manutenzione il ${when}.`
          : `${messageParts.join(
              ' e '
            )} durante una manutenzione il ${when}.`;

        try {
          await authFetch(`${GAS_API_BASE}/notifications`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'gas_usage',
              message,
              roles: ['admin', 'gestione', 'segreteria'],
            }),
          });
        } catch (err) {
          console.error(
            'Errore invio notifica utilizzo/recupero gas (MANUTENZIONI ASSEGNATE):',
            err
          );
        }
      }
    } catch (err) {
      console.error(
        'Errore generale durante il consumo/recupero gas (MANUTENZIONI ASSEGNATE):',
        err
      );
    }
  };

  // ======================
  // GENERA PDF + /work/complete-assigned
  // ======================
  const handleGeneratePdf = async () => {
    setError('');
    setSuccessMessage('');

    if (!formData.data || !formData.cliente) {
      setError('Compila almeno data e cliente prima di generare il PDF.');
      return;
    }

    setLoading(true);

    // SENZA RETE. Qui la lavorazione esiste gia' con il suo codice interno,
    // quindi il PDF che il cliente firma lo porta subito: non serve
    // aspettare la sincronizzazione ne' passare da init.
    if (senzaRete()) {
      try {
        const codice = fromWorkOrder?.codiceInterno || formData?.codiceInterno || '';

        await salvaOffline({
          tipo: TIPI.MANUTENZIONE,
          template: selectedTemplate,
          formData,
          checks,
          tecnicoSignature,
          clienteSignature,
          codiceInterno: codice,
          workOrderId,
          datiIniziali: null,
          rotte: { complete: '/api/work/complete-assigned' },
          extraCompletamento: { reportType: 'manutenzione' },
          summary: {
            codiceEsterno: fromWorkOrder?.codiceEsterno || '',
            data: formData.data || '',
            cliente: formData.cliente || '',
            impianto: formData.impianto || '',
            luogo: formData.luogo || '',
            modello: fromWorkOrder?.modello || '',
            matricola: fromWorkOrder?.seriale || '',
            inventario:
              formData.inventario ||
              fromWorkOrder?.inventario ||
              fromWorkOrder?.equipmentInventario ||
              '',
            note: formData.note || '',
          },
          gas: { gasUsedName, gasUsedAmount, gasRecoveredName, gasRecoveredAmount },
          nomeFile: `verbale_manutenzione_${codice || 'da-sincronizzare'}.pdf`,
        });

        setSuccessMessage(
          'Nessuna rete: il rapportino e salvato sul dispositivo e verra inviato da ' +
            'solo appena torni online, gas compreso. Il PDF appena scaricato e gia ' +
            'quello definitivo, con il codice interno.'
        );
      } catch (err) {
        console.error('Errore salvataggio offline:', err);
        setError(err.message || 'Non sono riuscito a salvare il rapportino sul dispositivo.');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const body = {
        template: selectedTemplate,
        formData,
        checks,
        tecnicoSignature,
        clienteSignature,
      };

      const blob = await generateManutenzionePdfBlob({ ...body, inviaReport: true });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const code =
        fromWorkOrder?.codiceInterno ||
        formData?.codiceInterno ||
        fromWorkOrder?.internalCode ||
        'MXX-00000';

      a.download = `verbale_manutenzione_${code}.pdf`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessage('PDF generato correttamente.');

      // aggiornamento gas
      await consumeGasAndNotify();

      // segna la lavorazione assegnata come "report compilato" + notifica + mail
      if (workOrderId) {
        try {
          const summary = {
            codiceInterno: fromWorkOrder.codiceInterno || '',
            codiceEsterno: fromWorkOrder.codiceEsterno || '',
            data: formData.data || '',
            cliente: formData.cliente || '',
            impianto: formData.impianto || '',
            luogo: formData.luogo || '',
            modello: fromWorkOrder.modello || '',
            matricola: fromWorkOrder.seriale || '',
            inventario: formData.inventario || fromWorkOrder.inventario || fromWorkOrder.equipmentInventory || '',
            note: formData.note || '',
          };

          const completeRes = await authFetch(
            `${API_BASE}/work/complete-assigned`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                workOrderId,
                reportType: 'manutenzione',
                summary,
                formData,
                checks,
                tecnicoSignature,
                clienteSignature,
              }),
            }
          );

          if (!completeRes.ok) {
            const data = await completeRes.json().catch(() => null);
            console.error(
              'Errore nel segnare la lavorazione come completata (MANUTENZIONI ASSEGNATE):',
              data || completeRes.status
            );
          }
        } catch (err2) {
          console.error(
            'Errore generale durante complete-assigned (MANUTENZIONI ASSEGNATE):',
            err2
          );
        }
      }
    } catch (err) {
      console.error('Errore generazione PDF manutenzione assegnata:', err);
      setError(err.message || 'Errore inatteso durante la generazione');
    } finally {
      setLoading(false);
    }
  };

  // ======================
  // RENDER
  // ======================
  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-8">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">
              Verbale di manutenzione (assegnata)
            </h1>
            <p className="text-sm text-gray-600">
              Compila il verbale a partire dalla lavorazione assegnata. I campi
              principali sono stati precompilati.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/report-assegnati')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Indietro
          </button>
        </div>

        {/* Template */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedTemplate(t.id)}
              className={`w-full text-left p-4 rounded-xl border transition shadow-sm ${
                selectedTemplate === t.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-blue-400'
              }`}
            >
              <h2 className="font-semibold text-gray-800">{t.label}</h2>
              <p className="text-xs text-gray-500 mt-1">
                Modello PDF: {t.file.replace('/Manutenzione/', '')}
              </p>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[2.1fr,3fr] gap-8">
          {/* SX: form + controlli + gas + firme */}
          <div className="space-y-6">
            {/* Dati intervento */}
            <Card className="shadow-sm p-4 overflow-hidden">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Dati intervento
              </h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Data
                  </label>
                  <input
                    type="date"
                    name="data"
                    value={formData.data}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Cliente
                  </label>
                  <input
                    type="text"
                    name="cliente"
                    value={formData.cliente}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Impianto / Presidio
                  </label>
                  <input
                    type="text"
                    name="impianto"
                    value={formData.impianto}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Luogo intervento
                  </label>
                  <input
                    type="text"
                    name="luogo"
                    value={formData.luogo}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Note (facoltative)
                </label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </Card>

            <DatiTrasferta formData={formData} onChange={handleChange} />
{/* GAS */}
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 space-y-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Gas utilizzato (solo per tracciamento interno)
              </h2>
              <p className="text-sm text-gray-500">
                Questi dati non vengono scritti sul PDF. Servono solo per scalare o
                incrementare i livelli gas del tuo furgone e avvisare l&apos;azienda.
              </p>

              {/* GAS UTILIZZATO */}
              <div className="mt-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Gas utilizzato
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Tipo di gas
                    </label>
                    <select
                      value={gasUsedName}
                      onChange={(e) => setGasUsedName(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="">Nessun gas utilizzato</option>
                      {availableGases.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Quantità utilizzata (g)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={gasUsedAmount}
                      onChange={(e) => setGasUsedAmount(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Es. 250"
                    />
                  </div>
                </div>
              </div>

              {/* GAS RECUPERATO */}
              <div className="mt-6 border-t border-gray-100 pt-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Gas recuperato (opzionale)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Tipo di gas
                    </label>
                    <select
                      value={gasRecoveredName}
                      onChange={(e) => setGasRecoveredName(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="">Nessun gas recuperato</option>
                      {availableGases.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Quantità recuperata (g)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={gasRecoveredAmount}
                      onChange={(e) =>
                        setGasRecoveredAmount(e.target.value)
                      }
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Es. 250"
                    />
                  </div>
                </div>
              </div>
            </section>


            {/* Verifiche */}
            <Card className="shadow-sm p-4 space-y-3">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">
                Verifiche eseguite
              </h3>
              <p className="text-xs text-gray-500 mb-2">
                Spunta le verifiche eseguite sull&apos;impianto. Alcune hanno &quot;N/A&quot; o
                valori da indicare.
              </p>

              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <div key={n} className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <Checkbox
                      checked={checks[n].ok}
                      onChange={() => toggleCheck(n, 'ok')}
                    />
                    <span>{CHECK_LABELS[n]}</span>
                  </label>
              
                  {n === 8 && (
                    <input
                      type="text"
                      value={checks[8].note || ''}
                      onChange={(e) => handleCheckValueChange(8, 'note', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                      placeholder="Valore / nota verifica 8 (facoltativo)"
                      disabled={!checks[8].ok}
                    />
                  )}
                </div>
              ))}



              {/* 9 con value + NA */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={checks[9].ok}
                    onChange={() => toggleCheck(9, 'ok')}
                  />
                  <span>Verifica 9: Controllo valori conducibilità dell’acqua demineralizzata: misurazione della linea o del serbatoio di alimentazione conducibilità:</span>
                </label>
                <div className="grid grid-cols-[1fr,auto] gap-2 items-center">
                  <input
                    type="text"
                    value={checks[9].value}
                    disabled={checks[9].na}
                    onChange={(e) =>
                      handleCheckValueChange(9, 'value', e.target.value)
                    }
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="Valore rilevato"
                  />
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    <Checkbox
                      checked={checks[9].na}
                      onChange={() => toggleCheck(9, 'na')}
                    />
                    N/A
                  </label>
                </div>
              </div>

              {/* 10 con NA */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={checks[10].ok}
                    onChange={() => toggleCheck(10, 'ok')}
                  />
                  <span>Verifica 10: Controllo dispositivi generatori di vapore: controllo caldaia, termostato, dispositivi di controllo</span>
                </label>
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={checks[10].na}
                    onChange={() => toggleCheck(10, 'na')}
                  />
                  N/A
                </label>
              </div>

              {/* 11 */}
              <div className="border-t border-gray-100 pt-2 mt-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={checks[11].ok}
                    onChange={() => toggleCheck(11, 'ok')}
                  />
                  <span>Verifica 11: Controllo dispositivi di sicurezza: simulazione intervento allarmi</span>
                </label>
              </div>

              {/* 12: T set / T mis + NA */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={checks[12].ok}
                    onChange={() => toggleCheck(12, 'ok')}
                  />
                  <span>Verifica 12: Controllo sistema di regolazione della Temperatura:</span>
                  <span>Misurazione della temperatura interna in centro camera:</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={checks[12].tSet}
                    onChange={(e) =>
                      handleCheckValueChange(12, 'tSet', e.target.value)
                    }
                    disabled={checks[12].na}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="T set"
                  />
                  <input
                    type="text"
                    value={checks[12].tMis}
                    onChange={(e) =>
                      handleCheckValueChange(12, 'tMis', e.target.value)
                    }
                    disabled={checks[12].na}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="T mis"
                  />
                </div>
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={checks[12].na}
                    onChange={() => toggleCheck(12, 'na')}
                  />
                  N/A
                </label>
              </div>

              {/* 13: UR set / UR mis + NA */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={checks[13].ok}
                    onChange={() => toggleCheck(13, 'ok')}
                  />
                  <span>Verifica 13: Controllo sistema di regolazione Umidità relativa:</span>
                  <span>Misurazione della UR% interna in centro camera:</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={checks[13].urSet}
                    onChange={(e) =>
                      handleCheckValueChange(13, 'urSet', e.target.value)
                    }
                    disabled={checks[13].na}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="UR set"
                  />
                  <input
                    type="text"
                    value={checks[13].urMis}
                    onChange={(e) =>
                      handleCheckValueChange(13, 'urMis', e.target.value)
                    }
                    disabled={checks[13].na}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                    placeholder="UR mis"
                  />
                </div>
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={checks[13].na}
                    onChange={() => toggleCheck(13, 'na')}
                  />
                  N/A
                </label>
              </div>

              {/* 14 con NA */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={checks[14].ok}
                    onChange={() => toggleCheck(14, 'ok')}
                  />
                  <span>Verifica 14: Controllo dispositivi di registrazione dati: controllo funzionamento eventuali dispositivi di acquisizione dati</span>
                </label>
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <Checkbox
                    checked={checks[14].na}
                    onChange={() => toggleCheck(14, 'na')}
                  />
                  N/A
                </label>
              </div>

              {/* 15 note */}
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox
                    checked={checks[15].ok}
                    onChange={() => toggleCheck(15, 'ok')}
                  />
                  <span>Verifica 15: ALTRO</span>
                </label>
                <textarea
                  value={checks[15].note}
                  onChange={(e) =>
                    handleCheckValueChange(15, 'note', e.target.value)
                  }
                  rows={2}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-full"
                  placeholder="Note aggiuntive"
                />
              </div>
            </Card>

            

            {/* FIRME + GENERA PDF */}
            <Card className="shadow-sm p-4 space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Firme</h3>
              <p className="text-xs text-gray-500 mb-1">
                Le firme non vengono mostrate sul PDF ma vengono inviate in digitale al
                backend.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {/* Firma tecnico */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">
                      Firma tecnico
                    </p>
                    <button
                      type="button"
                      onClick={() => openSignatureModal('tecnico')}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {tecnicoSignature ? 'Modifica firma' : 'Aggiungi firma'}
                    </button>
                  </div>
                  {tecnicoSignature ? (
                    <div className="border border-gray-300 rounded-lg p-2 bg-gray-50 flex items-center justify-between">
                      <span className="text-[11px] text-gray-600">
                        Firma tecnico salvata.
                      </span>
                      <button
                        type="button"
                        onClick={() => clearSignaturePreview('tecnico')}
                        className="text-[11px] text-gray-500 hover:text-gray-700"
                      >
                        Cancella firma
                      </button>
                    </div>
                  ) : (
                    <p className="text-[11px] text-gray-500">
                      Nessuna firma tecnica presente.
                    </p>
                  )}
                </div>

                {/* Firma cliente */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">
                      Firma cliente
                    </p>
                    <button
                      type="button"
                      onClick={() => openSignatureModal('cliente')}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {clienteSignature ? 'Modifica firma' : 'Aggiungi firma'}
                    </button>
                  </div>
                  {clienteSignature ? (
                    <div className="border border-gray-300 rounded-lg p-2 bg-gray-50 flex items-center justify-between">
                      <span className="text-[11px] text-gray-600">
                        Firma cliente salvata.
                      </span>
                      <button
                        type="button"
                        onClick={() => clearSignaturePreview('cliente')}
                        className="text-[11px] text-gray-500 hover:text-gray-700"
                      >
                        Cancella firma
                      </button>
                    </div>
                  ) : (
                    <p className="text-[11px] text-gray-500">
                      Nessuna firma cliente presente.
                    </p>
                  )}
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              {successMessage && (
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  {successMessage}
                </p>
              )}

              <button
                type="button"
                onClick={handleGeneratePdf}
                disabled={loading}
                className={`w-full py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm transition ${
                  loading
                    ? 'bg-blue-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Generazione in corso...' : 'Genera PDF compilato'}
              </button>
            </Card>
          </div>

          {/* DX: anteprima PDF */}
          <Card className="shadow-sm p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Anteprima modello
            </h3>
            {pdfUrl ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden h-[600px]">
                <iframe
                  title="Anteprima PDF Manutenzione"
                  src={pdfUrl}
                  className="w-full h-full"
                />
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Seleziona un modello per visualizzare l&apos;anteprima.
              </p>
            )}
          </Card>
        </div>
      </div>

      {/* Modal firma full-screen */}
      {signModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onTouchMove={(e) => e.preventDefault()}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-800">
                Firma {signModal.type === 'tecnico' ? 'tecnico' : 'cliente'}
              </h2>
              <button
                onClick={() => setSignModal({ open: false, type: null })}
                className="text-gray-500 hover:text-gray-800 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Firma con il dito o con il mouse. Al termine premi &quot;Salva
              firma&quot;.
            </p>
            <div className="border border-gray-300 rounded-lg bg-gray-50">
              <canvas
                ref={canvasRef}
                width={400}
                height={200}
                className="w-full h-48"
                onMouseDown={startDraw}
                onMouseMove={moveDraw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={moveDraw}
                onTouchEnd={endDraw}
              />
            </div>
            <div className="mt-3 flex justify-between items-center">
              <button
                type="button"
                onClick={() => {
                  const canvas = canvasRef.current;
                  if (!canvas) return;
                  const ctx = canvas.getContext('2d');
                  //ctx.fillStyle = '#ffffff';
                  //ctx.fillRect(0, 0, canvas.width, canvas.height);
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                }}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Cancella
              </button>
              <button
                type="button"
                onClick={saveSignature}
                className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
              >
                Salva firma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManutenzioniAssegnate;
